<template>
  <v-card>
    <template>
      <v-toolbar flat dark color="transparent" style="z-index: 1; width: 100%;" absolute>
        <v-btn v-if="!organization && !vessel" icon @click.native="$emit('toggleMode')">
          <v-icon>mdi-arrow-left</v-icon>
        </v-btn>
        <v-btn v-else-if="$vuetify.breakpoint.xsOnly" icon @click.native="$emit('close')">
          <v-icon>mdi-close</v-icon>
        </v-btn>
        <div class="flex-grow-1"></div>
        <v-btn v-if="$vuetify.breakpoint.xsOnly" icon @click="initialValues ? onSubmit(item) : openInvitationDialog()">
          <v-icon>mdi-check</v-icon>
        </v-btn>
      </v-toolbar>

      <div v-if="uploadingImage" class="dialog-spinner py-5">
        <v-progress-circular indeterminate size="50" color="primary"></v-progress-circular>
      </div>

      <div v-else-if="item" class="blue text-center pa-4 white--text" style="position: relative">
        <croppa
          v-model="myCroppa"
          class="mb-2"
          :initial-image="item.image ? item.image.contentUrl + '?x-request=html' : null"
          :width="150"
          :height="150"
          :quality="2"
          :accept="'image/*'"
          :placeholder="$t('message.choose_an_image')"
          :placeholder-font-size="14"
          @file-choose="imageChanged = true"
          @image-remove="imageChanged = true"
        ></croppa>
        <div class="headline">{{ item.firstName }} {{ item.lastName }}</div>
      </div>

      <template v-if="!uploadingImage">
        <v-card-text>
          <v-form ref="form" v-model="valid" lazy-validation @submit.prevent="onSubmit(item)">
            <v-text-field
              v-model="item.firstName"
              type="text"
              label="First Name"
              hint="The user first name"
              required
              :rules="firstNameRules"
              :error-messages="isInvalid('firstName') ? violations.firstName : []"
            ></v-text-field>
            <v-text-field
              v-model="item.lastName"
              type="text"
              label="Last Name"
              hint="The user last name"
              required
              :rules="lastNameRules"
              :error-messages="isInvalid('lastName') ? violations.lastName : []"
              @input.native="handleUpdateField('lastName', $event.target.value)"
            ></v-text-field>
            <v-text-field
              v-model="item.username"
              type="text"
              label="Username"
              hint="The user username"
              required
              :rules="usernameRules"
              :error-messages="isInvalid('username') ? violations.username : []"
              @input.native="handleUpdateField('username', $event.target.value)"
            ></v-text-field>
            <v-text-field
              v-model="item.email"
              type="text"
              label="Email"
              hint="The user email"
              required
              :rules="emailRules"
              :error-messages="isInvalid('email') ? violations.email : []"
              @input.native="handleUpdateField('email', $event.target.value)"
            ></v-text-field>
            <v-select
              v-model="item.locale"
              :items="locales"
              required
              :rules="localeRules"
              item-value="value"
              item-text="text"
              :label="$t('message.language')"
              :hint="$t('message.user_language')"
              single-line
            ></v-select>

            <template v-if="!vessel">
              <v-divider class="my-3 hacked-divider"></v-divider>
              <v-list-item-title>
                {{ $t('message.role_in_organizations') }}
              </v-list-item-title>
              <v-card
                v-for="roleInOrganization in item.roleInOrganizations"
                :key="roleInOrganization['@id']"
                class="mt-3 elevation-0"
                color="grey lighten-5"
              >
                <v-card-text>
                  <div v-if="organization">
                    {{ organization.name }}
                    <v-text-field
                      v-model="roleInOrganization.organization"
                      style="display: none"
                      :value="organization['@id']"
                    />
                  </div>
                  <div v-else-if="vessel">
                    {{ vessel.organization.name }}
                    <v-text-field
                      v-model="roleInOrganization.organization"
                      style="display: none"
                      :value="vessel.organization['@id']"
                    />
                  </div>
                  <div v-else-if="organizations && organizations.length === 1">
                    {{ organizations[0].name }}
                    <v-text-field
                      v-model="roleInOrganization.organization"
                      style="display: none"
                      :value="organizations[0]['@id']"
                    />
                  </div>
                  <v-select
                    v-else-if="organizations && organizations.length > 1"
                    v-model="roleInOrganization.organization"
                    :placeholder="$t('message.organization')"
                    :items="organizations"
                    item-value="@id"
                    item-text="name"
                    :menu-props="{ maxHeight: '400' }"
                    :rules="roleInOrganizationsOrganizationRules"
                  ></v-select>
                  <v-select
                    v-if="organizationRoles"
                    v-model="roleInOrganization.organizationRole"
                    :placeholder="$t('message.organization_role')"
                    :items="organizationRoles"
                    item-value="@id"
                    :item-text="translateRole"
                    :menu-props="{ maxHeight: '400' }"
                    return-object
                  ></v-select>
                </v-card-text>
                <v-card-actions v-if="item.roleInOrganizations.length > 1">
                  <v-btn text color="red" @click="removeRoleInOrganization(roleInOrganization)">{{
                    $t('message.remove')
                  }}</v-btn>
                </v-card-actions>
              </v-card>
              <v-btn
                v-if="organizations && !organization && !vessel"
                block
                class="ma-0 mt-3"
                :dark="item.roleInOrganizations.length < organizations.length"
                color="blue"
                text
                :disabled="item.roleInOrganizations.length >= organizations.length"
                @click="addRoleInOrganization"
                >{{ $t('message.add_role_in_organization') }}</v-btn
              >
            </template>

            <template v-if="allowedVessels && allowedVessels.length">
              <v-divider v-if="!vessel" class="my-3 hacked-divider"></v-divider>
              <v-list-item-title v-if="!vessel">
                {{ $t('message.role_in_vessels') }}
              </v-list-item-title>
              <v-card
                v-for="roleInVessel in item.roleInVessels"
                :key="roleInVessel['@id']"
                class="mt-3 elevation-0"
                color="grey lighten-5"
              >
                <v-card-text>
                  <div v-if="vessel">
                    {{ vessel.name }}
                    <v-text-field v-model="roleInVessel.vessel" style="display: none" :value="vessel['@id']" />
                  </div>
                  <div v-else-if="allowedVessels && allowedVessels.length === 1">
                    {{ allowedVessels[0].name }}
                    <v-text-field
                      v-model="roleInVessel.vessel"
                      style="display: none"
                      :value="allowedVessels[0]['@id']"
                    />
                  </div>
                  <v-select
                    v-else-if="allowedVessels"
                    v-model="roleInVessel.vessel"
                    :placeholder="$t('message.vessel')"
                    :items="allowedVessels"
                    item-value="@id"
                    item-text="name"
                    :menu-props="{ maxHeight: '400' }"
                    :rules="roleInVesselsVesselRules"
                  ></v-select>
                  <v-select
                    v-if="vesselRoles"
                    v-model="roleInVessel.vesselRole"
                    :placeholder="$t('message.vessel_role')"
                    :items="vesselRoles"
                    item-value="@id"
                    :item-text="translateRole"
                    :menu-props="{ maxHeight: '400' }"
                  ></v-select>
                </v-card-text>
                <v-card-actions v-if="!vessel">
                  <v-btn text color="red" @click="removeRoleInVessel(roleInVessel)">{{ $t('message.remove') }}</v-btn>
                </v-card-actions>
              </v-card>
              <v-btn
                v-if="vessels && !vessel"
                block
                text
                class="ma-0 mt-3"
                :dark="item.roleInVessels.length < vessels.length"
                color="blue"
                :disabled="item.roleInVessels.length >= vessels.length"
                @click="addRoleInVessel"
                >{{ $t('message.add_role_in_vessel') }}</v-btn
              >
            </template>
          </v-form>
        </v-card-text>

        <v-divider class="hidden-xs-only"></v-divider>
        <v-card-actions class="hidden-xs-only">
          <div class="flex-grow-1"></div>
          <v-btn color="grey" text @click.native="$emit('close')">{{ $t('message.close') }}</v-btn>
          <v-btn
            :disabled="!valid"
            color="orange"
            text
            @click="initialValues ? onSubmit(item) : openInvitationDialog()"
          >
            {{ $t('message.save') }}
          </v-btn>
        </v-card-actions>
      </template>
    </template>

    <v-dialog v-model="invitationDialog" max-width="290">
      <v-card>
        <v-card-title class="headline">{{ $t('message.invitation_dialog.title') }}</v-card-title>

        <v-card-text>
          {{ $t('message.invitation_dialog.paragraph', { email: item.email }) }}
        </v-card-text>

        <v-card-actions>
          <div class="flex-grow-1"></div>

          <v-btn color="grey" text @click="invitationDialog = false">
            {{ $t('message.close') }}
          </v-btn>

          <v-btn color="green darken-1" text @click="onSubmit(item)">
            {{ $t('message.create') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>

<script>
import fetch from '@/utils/fetch'

export default {
  props: {
    handleSubmit: {
      type: Function,
      required: true
    },

    handleUpdateField: {
      type: Function,
      required: true
    },

    handleDelete: {
      type: Function,
      required: false,
      default: () => {}
    },

    values: {
      type: Object,
      required: true
    },

    errors: {
      type: Object,
      default: () => {}
    },

    initialValues: {
      type: Object,
      default: () => {}
    },

    vessel: {
      type: Object,
      required: false,
      default: null
    },

    organization: {
      type: Object,
      required: false,
      default: null
    }
  },
  data() {
    return {
      imageChanged: false,
      uploadingImage: false,
      myCroppa: {},
      valid: true,
      invitationDialog: false,
      locales: [
        {
          text: this.$t('message.english'),
          value: 'en'
        },
        {
          text: this.$t('message.spanish'),
          value: 'es'
        }
      ],
      usernameRules: [
        v => {
          return !!v || 'Username is required'
        },
        v => {
          return (v && v.length <= 32) || 'Username must be less than 32 characters'
        },
        v => {
          return (v && v.length >= 4) || 'Username must be more than 4 characters'
        },
        v => {
          return (
            /^[a-z0-9_.]*$/.test(v) ||
            'Username must be lowercase and contain only letters, numbers, dots and underscores'
          )
        }
      ],
      firstNameRules: [
        v => {
          return !!v || 'First name is required'
        }
      ],
      lastNameRules: [
        v => {
          return !!v || 'Last name is required'
        }
      ],
      localeRules: [
        v => {
          return !!v || 'Language is required'
        }
      ],
      emailRules: [
        value => {
          const pattern = /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
          return pattern.test(value) || 'Invalid e-mail.'
        }
      ],
      roleInOrganizationsOrganizationRules: [
        organization => {
          let count = 0
          this.item.roleInOrganizations.forEach(roleInOrganization => {
            const id1 = 'string' === typeof organization ? organization : organization['@id']
            const id2 =
              'string' === typeof roleInOrganization.organization
                ? roleInOrganization.organization
                : roleInOrganization.organization['@id']
            if (id1 === id2) {
              count++
            }
          })
          return count <= 1 || 'This organization already has an assigned role'
        }
      ],
      roleInVesselsVesselRules: [
        vessel => {
          let count = 0
          this.item.roleInVessels.forEach(roleInVessel => {
            const id1 = 'string' === typeof vessel ? vessel : vessel['@id']
            const id2 = 'string' === typeof roleInVessel.vessel ? roleInVessel.vessel : roleInVessel.vessel['@id']
            if (id1 === id2) {
              count++
            }
          })
          return count <= 1 || 'This vessel already has an assigned role'
        }
      ],
      organizations: null,
      organizationRoles: null,
      vessels: null,
      vesselRoles: null
    }
  },

  computed: {
    // eslint-disable-next-line
            item () {
      return this.initialValues || this.values
    },

    violations() {
      return this.errors || {}
    },

    allowedVessels() {
      if (!this.vessels) {
        return null
      }
      return this.vessels.filter(vessel => {
        let allowed = false
        this.item.roleInOrganizations.forEach(roleInOrganization => {
          let organizationId =
            'string' === typeof roleInOrganization.organization
              ? roleInOrganization.organization
              : roleInOrganization.organization['@id']
          let organizationRoleName = roleInOrganization.organizationRole.name

          if (vessel.organization['@id'] === organizationId && organizationRoleName === 'ROLE_MEMBER') {
            allowed = true
          }
        })

        return allowed
      })
    },

    rolesReady() {
      return this.organizationRoles && this.vesselRoles
    }
  },

  watch: {
    rolesReady: function(rolesReady) {
      if (!rolesReady) {
        return
      }
      let memberRole = null

      this.organizationRoles.forEach(role => {
        if ('ROLE_MEMBER' === role.name) {
          memberRole = role
        }
      })

      if (this.organization && false === this.item.hasOwnProperty('@id')) {
        this.item.roleInOrganizations = []
        this.item.roleInOrganizations.push({
          organization: this.organization['@id'],
          organizationRole: this.organizationRoles[0]
        })
      }

      if (this.vessel && false === this.item.hasOwnProperty('@id')) {
        this.item.roleInOrganizations = []
        this.item.roleInVessels = []
        this.item.roleInVessels.push({
          vessel: this.vessel,
          vesselRole: this.vesselRoles[0]
        })

        this.item.roleInOrganizations.push({
          organization: this.vessel.organization,
          organizationRole: memberRole
        })
      }
    }
  },

  created() {
    this.fetchOrganizations()
    this.fetchOrganizationRoles()
    this.fetchVessels()
    this.fetchVesselRoles()
  },

  methods: {
    translateRole(role) {
      return this.$t('message.' + role.name)
    },
    removeRoleInOrganization(roleInOrganizationToDelete) {
      this.item.roleInOrganizations.forEach((roleInOrganization, index) => {
        if (roleInOrganization === roleInOrganizationToDelete) {
          delete this.item.roleInOrganizations.splice(index, 1)
        }
      })
    },

    removeRoleInVessel(roleInVesselToDelete) {
      this.item.roleInVessels.forEach((roleInVessel, index) => {
        if (roleInVessel === roleInVesselToDelete) {
          delete this.item.roleInVessels.splice(index, 1)
        }
      })
    },

    addRoleInOrganization() {
      this.item.roleInOrganizations.push({
        organization: this.organizations[0],
        organizationRole: this.organizationRoles[0]
      })
    },

    addRoleInVessel() {
      this.item.roleInVessels.push({
        vessel: this.vessels[0],
        vesselRole: this.vesselRoles[0]
      })
    },

    fetchOrganizations() {
      fetch('/organizations?pagination=false')
        .then(response => response.json())
        .then(data => {
          this.organizations = data['hydra:member']
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
    },

    fetchOrganizationRoles() {
      fetch('/organization_roles?pagination=false')
        .then(response => response.json())
        .then(data => {
          this.organizationRoles = data['hydra:member']
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
    },

    fetchVessels() {
      fetch('/vessels?pagination=false&archived=false')
        .then(response => response.json())
        .then(data => {
          this.vessels = data['hydra:member']
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
    },

    fetchVesselRoles() {
      fetch('/vessel_roles?pagination=false')
        .then(response => response.json())
        .then(data => {
          this.vesselRoles = data['hydra:member']
        })
        .catch(e => {
          // eslint-disable-next-line no-console
          console.log(e)
        })
    },

    isInvalid(key) {
      return Object.keys(this.violations).length > 0 && this.violations[key]
    },

    isAllowed(vessel) {
      let isAllowed = false
      const vesselId = 'string' === typeof vessel ? vessel : vessel['@id']

      this.allowedVessels.forEach(allowedVessel => {
        if (allowedVessel['@id'] === vesselId) {
          isAllowed = true
        }
      })

      return isAllowed
    },

    onDeleteConfirm() {
      this.deleteConfirmShow = false
      this.handleDelete()
    },

    onDeleteCancel() {
      this.deleteConfirmShow = false
    },

    openInvitationDialog() {
      if (this.$refs.form.validate()) {
        this.invitationDialog = true
      }
    },

    onSubmit(item) {
      this.invitationDialog = false
      if (this.$refs.form.validate()) {
        let cleanItem = { ...item }

        cleanItem.roleInVessels.forEach((roleInVessel, index) => {
          if (!this.isAllowed(roleInVessel.vessel)) {
            cleanItem.roleInVessels.splice(index, 1)
          }
        })

        cleanItem.roleInVessels.forEach(roleInVessel => {
          if ('string' !== typeof roleInVessel.vessel) {
            roleInVessel.vessel = roleInVessel.vessel['@id']
          }

          if ('string' !== typeof roleInVessel.vesselRole) {
            roleInVessel.vesselRole = roleInVessel.vesselRole['@id']
          }
        })

        cleanItem.roleInOrganizations.forEach(roleInOrganization => {
          if ('string' !== typeof roleInOrganization.organization) {
            roleInOrganization.organization = roleInOrganization.organization['@id']
          }

          if ('string' !== typeof roleInOrganization.organizationRole) {
            roleInOrganization.organizationRole = roleInOrganization.organizationRole['@id']
          }
        })

        this.myCroppa.generateBlob(
          blob => {
            if (this.imageChanged) {
              if (blob) {
                let formData = new FormData()
                formData.append('file', blob)

                this.uploadingImage = true

                return fetch('/media_objects', {
                  method: 'POST',
                  body: formData
                })
                  .then(response => {
                    this.uploadingImage = false
                    return response.json()
                  })
                  .then(data => {
                    cleanItem.image = data
                    this.handleSubmit(cleanItem)
                  })
                  .catch(e => {
                    // eslint-disable-next-line no-console
                    console.log(e)
                  })
              } else {
                cleanItem.image = null
                this.handleSubmit(cleanItem)
              }
            } else {
              this.handleSubmit(cleanItem)
            }
          },
          null,
          0.8
        )
      }
    }
  }
}
</script>

<style lang="stylus" scoped>
.hacked-divider {
  max-width: calc(100% + 32px);
  width: calc(100% + 32px);
  margin-left: -16px;
}
</style>
